import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useToast } from '../context/ToastProvider';
import { reportFeedPost } from '../services/feedApi';
import { getAuthToken } from '../utils/auth';
import { localizedValue } from '../localization/presentationLanguage';
import { platform } from '../platform/identity';
import { openExternal, registerBackHandler } from '../platform/navigation';
const feedText = (es, en) => localizedValue({ es, en });

const actionBaseStyle = {
  appearance: 'none',
  WebkitAppearance: 'none',
  border: 'none',
  background: 'transparent',
  padding: 0,
  margin: 0,
  font: 'inherit',
  color: 'inherit',
};

const socialButtonStyle = {
  ...actionBaseStyle,
  minHeight: 34,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 7,
  padding: '6px 10px',
  borderRadius: 999,
  cursor: 'pointer',
  userSelect: 'none',
  touchAction: 'manipulation',
  transition:
    'transform 180ms cubic-bezier(0.22, 1, 0.36, 1), background 180ms ease, border-color 180ms ease, box-shadow 180ms ease, color 180ms ease, opacity 180ms ease, filter 180ms ease',
  WebkitTapHighlightColor: 'transparent',
};

const iconWrapStyle = {
  width: 20,
  height: 20,
  borderRadius: 999,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: '0 0 auto',
  transition: 'transform 180ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 180ms ease, background 180ms ease, filter 180ms ease',
};

const labelStyle = {
  fontSize: 12,
  fontWeight: 900,
  letterSpacing: 0.03,
  whiteSpace: 'nowrap',
};

const countStyle = {
  fontSize: 11.5,
  fontWeight: 800,
  opacity: 0.62,
  whiteSpace: 'nowrap',
};

const shareTileStyle = {
  ...actionBaseStyle,
  borderRadius: 15,
  padding: '12px 10px 11px',
  cursor: 'pointer',
  background: 'linear-gradient(180deg, rgba(255,255,255,0.055), rgba(255,255,255,0.024))',
  border: '1px solid rgba(255,224,130,0.08)',
  color: '#F6F0FF',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  fontWeight: 900,
  fontSize: 12,
  minHeight: 84,
  boxShadow: '0 12px 20px rgba(0,0,0,0.14), 0 0 0 1px rgba(255,255,255,0.025) inset',
  transition: 'transform 180ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 180ms ease, border-color 180ms ease, background 180ms ease, filter 180ms ease',
  WebkitTapHighlightColor: 'transparent',
};

const shareTileIconWrapStyle = {
  width: 36,
  height: 36,
  borderRadius: 999,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 0 0 1px rgba(255,255,255,0.08) inset',
  fontSize: 17,
  lineHeight: 1,
};

function applyHoverIn(event) {
  event.currentTarget.style.transform = 'translateY(-1px) scale(1.01)';
}

function applyHoverOut(event) {
  event.currentTarget.style.transform = 'translateY(0px) scale(1)';
}

function applyTileHoverIn(event) {
  event.currentTarget.style.transform = 'translateY(-2px) scale(1.01)';
  event.currentTarget.style.filter = 'brightness(1.04)';
}

function applyTileHoverOut(event) {
  event.currentTarget.style.transform = 'translateY(0px) scale(1)';
  event.currentTarget.style.filter = 'brightness(1)';
}

function applyPressIn(event) {
  event.currentTarget.style.transform = 'translateY(0px) scale(0.97)';
}

function applyPressOut(event) {
  event.currentTarget.style.transform = 'translateY(0px) scale(1)';
}

function getPostUrl(post) {
  const postId = String(post?.id || '');
  if (!postId || typeof window === 'undefined') return '';
  const url = new URL(window.location.href);
  url.searchParams.set('post', postId);
  return url.toString();
}

function getShareText(post) {
  const rawText = String(post?.text || '').replace(/\s+/g, ' ').trim();
  if (!rawText) return feedText('Mirá esta publicación en Zodiacus ✨', 'View this post on Zodiacus ✨');
  const trimmed = rawText.length > 110 ? `${rawText.slice(0, 107)}...` : rawText;
  return `${trimmed} ✨`;
}

function openShareWindow(url, title = 'Compartir') {
  if (!url) return;
  if (platform.isNative) {
    openExternal(url).catch(() => {});
    return;
  }
  if (typeof window === 'undefined') return;

  const width = 620;
  const height = 720;
  const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - width) / 2));
  const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - height) / 2));
  const features = [
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    'scrollbars=yes',
    'resizable=yes',
    'toolbar=no',
    'menubar=no',
    'location=yes',
    'status=no',
  ].join(',');

  const popup = window.open(url, title, features);
  if (popup && typeof popup.focus === 'function') {
    popup.focus();
    return;
  }

  window.open(url, '_blank', 'noopener,noreferrer');
}

function buildWhatsappShareUrl(post, shareUrl) {
  const text = getShareText(post);
  return `https://wa.me/?text=${encodeURIComponent(`${text} ${shareUrl}`.trim())}`;
}

function buildFacebookShareUrl(shareUrl) {
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
}

function buildTwitterShareUrl(post, shareUrl) {
  const text = getShareText(post);
  return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
}

function buildTelegramShareUrl(post, shareUrl) {
  const text = getShareText(post);
  return `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}`;
}

export default function FeedPostActions({
  post,
  onLike,
  onComment,
  onDelete,
  liking = false,
  deleting = false,
  commentCountOverride = null,
  likeCountOverride = null,
  className = '',
}) {
    const [menuOpen, setMenuOpen] = useState(false);
  const [menuFeedback, setMenuFeedback] = useState('');
  const [shareSheetOpen, setShareSheetOpen] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [reportComposerOpen, setReportComposerOpen] = useState(false);
  const [reportDetails, setReportDetails] = useState('');
  const [hoveredAction, setHoveredAction] = useState('');
  const [pressedAction, setPressedAction] = useState('');
  const [likeBurstActive, setLikeBurstActive] = useState(false);
  
    const rootRef = useRef(null);
  const menuRef = useRef(null);
  const mobileSheetRef = useRef(null);
  
  const { showToast } = useToast();

  useEffect(() => {
    if (!menuOpen && !shareSheetOpen && !reportComposerOpen) return undefined;
    return registerBackHandler(() => {
      if (reportComposerOpen) setReportComposerOpen(false);
      else if (shareSheetOpen) setShareSheetOpen(false);
      else setMenuOpen(false);
      return true;
    });
  }, [menuOpen, reportComposerOpen, shareSheetOpen]);

  const liked = !!post?.viewer?.liked;
  const isOwner = !!post?.viewer?.isOwner;
  const likeCount = Number(likeCountOverride ?? post?.stats?.likeCount ?? 0);
  const commentCount = Number(commentCountOverride ?? post?.stats?.commentCount ?? 0);
  const [viewportWidth, setViewportWidth] = useState(() => {
    if (typeof window === 'undefined') return 1280;
    return window.innerWidth || 1280;
  });

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleResize = () => {
      setViewportWidth(window.innerWidth || 1280);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const shareUrl = useMemo(() => getPostUrl(post), [post]);

  
  const isMobile = viewportWidth <= 640;
  const canUsePortal = typeof document !== 'undefined' && !!document.body;
  const likeGlowActive = liked || hoveredAction === 'like' || pressedAction === 'like';
  const commentGlowActive = hoveredAction === 'comment' || pressedAction === 'comment';
  const menuGlowActive = menuOpen || hoveredAction === 'menu' || pressedAction === 'menu';

  

  useEffect(() => {
    if (!menuOpen) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;

      if (isMobile) {
        if (mobileSheetRef.current && mobileSheetRef.current.contains(target)) {
          return;
        }
      } else if (menuRef.current && menuRef.current.contains(target)) {
        return;
      }

      setMenuOpen(false);
      setShareSheetOpen(false);
      setReportComposerOpen(false);
      setReportDetails('');
      
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        if (shareSheetOpen) {
          setShareSheetOpen(false);
          return;
        }
        setMenuOpen(false);
        setReportComposerOpen(false);
        setReportDetails('');
        
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [menuOpen, shareSheetOpen, reportComposerOpen, isMobile]);

  useEffect(() => {
    if (!menuFeedback) return undefined;
    const timeout = window.setTimeout(() => setMenuFeedback(''), 1800);
    return () => window.clearTimeout(timeout);
  }, [menuFeedback]);

    useEffect(() => {
    if (!liked) {
      setLikeBurstActive(false);
      return undefined;
    }

    setLikeBurstActive(true);
    const timeout = window.setTimeout(() => {
      setLikeBurstActive(false);
    }, 520);

    return () => window.clearTimeout(timeout);
  }, [liked]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const clearPressed = () => {
      setPressedAction('');
    };

    window.addEventListener('pointerup', clearPressed);
    window.addEventListener('pointercancel', clearPressed);
    window.addEventListener('blur', clearPressed);

    return () => {
      window.removeEventListener('pointerup', clearPressed);
      window.removeEventListener('pointercancel', clearPressed);
      window.removeEventListener('blur', clearPressed);
    };
  }, []);


  const handleLike = async () => {
    if (typeof onLike !== 'function' || liking || deleting) return;
    await onLike(post);
  };

  const handleComment = () => {
    if (typeof onComment !== 'function') return;
    onComment(post);
  };

  const handleDelete = async () => {
    if (!isOwner || typeof onDelete !== 'function' || deleting) return;
    setMenuOpen(false);
    setShareSheetOpen(false);
    setReportComposerOpen(false);
    setReportDetails('');

    try {
      await onDelete(post);
      showToast(feedText('Publicación eliminada', 'Post deleted'), { type: 'success' });
    } catch (err) {
      showToast(err?.message || 'No se pudo eliminar', { type: 'error' });
      throw err;
    }
  };

  const handleCopyLink = async () => {
    try {
      if (!shareUrl) throw new Error('No link');
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setMenuFeedback('Enlace copiado');
          setShareSheetOpen(false);
                  setReportComposerOpen(false);
        showToast('Enlace copiado', { type: 'success', duration: 1800 });
      } else {
        throw new Error('Clipboard unavailable');
      }
    } catch {
      setMenuFeedback('No se pudo copiar');
      showToast('No se pudo copiar el enlace', { type: 'error' });
    }
  };


  const handleOpenShareSheet = () => {
    setReportComposerOpen(false);
    setReportDetails('');
    setShareSheetOpen((prev) => !prev);
  };
    
      const handleOpenReportComposer = () => {
    setShareSheetOpen(false);
    setReportComposerOpen((prev) => {
      const next = !prev;
      if (!next) setReportDetails('');
      return next;
    });
  };

  const handleCloseReportComposer = () => {
    setReportComposerOpen(false);
    setReportDetails('');
  };

  const handleShareWhatsapp = () => {
    if (!shareUrl) {
      showToast('No se pudo generar el enlace', { type: 'error' });
      return;
    }
    setShareSheetOpen(false);
    setReportComposerOpen(false);
    openShareWindow(buildWhatsappShareUrl(post, shareUrl), 'Compartir en WhatsApp');
    setMenuFeedback('WhatsApp listo');
    showToast('Abriendo WhatsApp', { type: 'success', duration: 1800 });
    setMenuOpen(false);
  };

  const handleShareFacebook = () => {
    if (!shareUrl) {
      showToast('No se pudo generar el enlace', { type: 'error' });
      return;
    }
    setShareSheetOpen(false);
    setReportComposerOpen(false);
    openShareWindow(buildFacebookShareUrl(shareUrl), 'Compartir en Facebook');
    setMenuFeedback('Facebook listo');
    showToast('Abriendo Facebook', { type: 'success', duration: 1800 });
    setMenuOpen(false);
  };

  const handleShareTwitter = () => {
    if (!shareUrl) {
      showToast('No se pudo generar el enlace', { type: 'error' });
      return;
    }
    setShareSheetOpen(false);
    setReportComposerOpen(false);
    openShareWindow(buildTwitterShareUrl(post, shareUrl), 'Compartir en X');
    setMenuFeedback('X listo');
    showToast('Abriendo X', { type: 'success', duration: 1800 });
    setMenuOpen(false);
  };

  const handleShareTelegram = () => {
    if (!shareUrl) {
      showToast('No se pudo generar el enlace', { type: 'error' });
      return;
    }
    setShareSheetOpen(false);
    setReportComposerOpen(false);
    openShareWindow(buildTelegramShareUrl(post, shareUrl), 'Compartir en Telegram');
    setMenuFeedback('Telegram listo');
    showToast('Abriendo Telegram', { type: 'success', duration: 1800 });
    setMenuOpen(false);
  };

    const handleReport = async () => {
    const postId = String(post?.id || '').trim();
    if (!postId || reporting) return;

    try {
      setReporting(true);

      const token = getAuthToken();

      const result = await reportFeedPost({
        token,
        postId,
        reason: 'other',
        details: reportDetails.trim(),
      });

      setMenuFeedback(result?.alreadyReported ? feedText('Ya reportado', 'Already reported') : feedText('Reporte enviado', 'Report sent'));
      setReportComposerOpen(false);
      setReportDetails('');
      setMenuOpen(false);

      showToast(
        result?.alreadyReported
          ? feedText('Ya habías reportado esta publicación.', 'You already reported this post.')
          : 'Reporte enviado correctamente.',
        { type: 'success', duration: 2200 }
      );
    } catch (err) {
      showToast(err?.message || feedText('No se pudo reportar la publicación.', 'The post could not be reported.'), {
        type: 'error',
        duration: 2200,
      });
    } finally {
      setReporting(false);
    }
  };


  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (!menuOpen || !isMobile) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previousTouchAction = document.body.style.touchAction;

    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.touchAction = previousTouchAction;
    };
  }, [menuOpen, isMobile]);
  return (
    <div
      ref={rootRef}
      className={`feed-post-actions ${className}`.trim()}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        marginTop: isMobile ? 4 : 8,
        flexWrap: 'nowrap',
                position: 'relative',
        zIndex: 1,
        isolation: 'auto',
        transition: 'transform 180ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? 4 : 6,
          flexWrap: 'nowrap',
          minWidth: 0,
          flex: '1 1 auto',
          overflowX: 'auto',
          overflowY: 'visible',
          scrollbarWidth: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <button
          type="button"
          onClick={handleLike}
          onMouseEnter={(event) => {
            setHoveredAction('like');
            applyHoverIn(event);
          }}
          onMouseLeave={(event) => {
            setHoveredAction((prev) => (prev === 'like' ? '' : prev));
            applyHoverOut(event);
          }}
          onMouseDown={(event) => {
            setPressedAction('like');
            applyPressIn(event);
          }}
          onMouseUp={(event) => {
            setPressedAction((prev) => (prev === 'like' ? '' : prev));
            applyPressOut(event);
          }}
          onTouchStart={(event) => {
            setPressedAction('like');
            applyPressIn(event);
          }}
          onTouchEnd={(event) => {
            setPressedAction((prev) => (prev === 'like' ? '' : prev));
            applyPressOut(event);
          }}
          disabled={liking || deleting}
          aria-pressed={liked}
          style={{
            ...socialButtonStyle,
            minHeight: isMobile ? 32 : socialButtonStyle.minHeight,
            padding: isMobile ? '6px 9px' : '6px 10px',
            position: 'relative',
            overflow: 'hidden',
            background: liked
              ? 'linear-gradient(180deg, rgba(255,224,130,0.16), rgba(255,224,130,0.08))'
              : likeGlowActive
                ? 'linear-gradient(180deg, rgba(255,255,255,0.09), rgba(255,255,255,0.045))'
                : 'rgba(255,255,255,0.035)',
            border: liked
              ? '1px solid rgba(255,224,130,0.24)'
              : likeGlowActive
                ? '1px solid rgba(255,224,130,0.14)'
                : '1px solid rgba(255,255,255,0.06)',
            color: liked ? '#FFE39A' : '#F3EEFF',
            boxShadow: liked
              ? '0 12px 22px rgba(0,0,0,0.12), 0 0 18px rgba(255,224,130,0.14)'
              : likeGlowActive
                ? '0 10px 18px rgba(0,0,0,0.08), 0 0 14px rgba(255,224,130,0.08)'
                : 'none',
            opacity: liking ? 0.86 : 1,
            filter: liking ? 'brightness(1.03)' : 'none',
            flex: '0 0 auto',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              background:
                'linear-gradient(135deg, rgba(255,224,130,0.10), rgba(255,255,255,0.02) 34%, rgba(170,124,255,0.08) 72%, rgba(255,224,130,0.05))',
              opacity: likeGlowActive ? 1 : 0.72,
              transition: 'opacity 180ms ease',
            }}
          />
          <span
            aria-hidden="true"
            className={likeBurstActive ? 'feed-post-actions__burst is-active' : 'feed-post-actions__burst'}
          />
          <span
            aria-hidden="true"
            style={{
              ...iconWrapStyle,
              position: 'relative',
              zIndex: 1,
              background: liked
                ? 'rgba(255,255,255,0.12)'
                : likeGlowActive
                  ? 'rgba(255,255,255,0.09)'
                  : 'rgba(255,255,255,0.06)',
              boxShadow: liked
                ? '0 0 0 1px rgba(255,255,255,0.08) inset, 0 0 12px rgba(255,224,130,0.10)'
                : likeGlowActive
                  ? '0 0 0 1px rgba(255,255,255,0.06) inset, 0 0 10px rgba(255,224,130,0.06)'
                  : '0 0 0 1px rgba(255,255,255,0.04) inset',
              transform: liked ? 'scale(1.03)' : likeGlowActive ? 'scale(1.02)' : 'scale(1)',
            }}
          >
            <span
              style={{
                position: 'relative',
                zIndex: 1,
                fontSize: 13,
                lineHeight: 1,
                transform: liking ? 'scale(1.16)' : liked ? 'scale(1.10)' : likeGlowActive ? 'scale(1.04)' : 'scale(1)',
                transition: 'transform 200ms cubic-bezier(0.22, 1, 0.36, 1), filter 180ms ease',
                filter: liked ? 'drop-shadow(0 0 10px rgba(255,224,130,0.24))' : 'none',
              }}
            >
              {liked ? '♥' : '♡'}
            </span>
          </span>

          <span style={{ ...labelStyle, position: 'relative', zIndex: 1 }}>Me gusta</span>
          <span style={{ ...countStyle, position: 'relative', zIndex: 1, opacity: liked ? 0.86 : countStyle.opacity }}>{likeCount}</span>
        </button>

        <button
          type="button"
          onClick={handleComment}
          onMouseEnter={(event) => {
            setHoveredAction('comment');
            applyHoverIn(event);
          }}
          onMouseLeave={(event) => {
            setHoveredAction((prev) => (prev === 'comment' ? '' : prev));
            applyHoverOut(event);
          }}
          onMouseDown={(event) => {
            setPressedAction('comment');
            applyPressIn(event);
          }}
          onMouseUp={(event) => {
            setPressedAction((prev) => (prev === 'comment' ? '' : prev));
            applyPressOut(event);
          }}
          onTouchStart={(event) => {
            setPressedAction('comment');
            applyPressIn(event);
          }}
          onTouchEnd={(event) => {
            setPressedAction((prev) => (prev === 'comment' ? '' : prev));
            applyPressOut(event);
          }}
          disabled={deleting}
          aria-label={commentCount > 0 ? `Abrir hilo con ${commentCount} respuestas` : 'Abrir hilo'}
          style={{
            ...socialButtonStyle,
            minHeight: isMobile ? 32 : socialButtonStyle.minHeight,
            minWidth: isMobile ? 36 : 38,
            padding: isMobile ? '6px 10px' : '6px 11px',
            position: 'relative',
            overflow: 'hidden',
            background: commentGlowActive
              ? 'linear-gradient(180deg, rgba(255,255,255,0.09), rgba(255,255,255,0.045))'
              : 'rgba(255,255,255,0.035)',
            border: commentGlowActive
              ? '1px solid rgba(255,224,130,0.12)'
              : '1px solid rgba(255,255,255,0.06)',
            color: '#F3EEFF',
            boxShadow: commentGlowActive
              ? '0 10px 18px rgba(0,0,0,0.08), 0 0 14px rgba(255,224,130,0.06)'
              : 'none',
            opacity: deleting ? 0.72 : 1,
            flex: '0 0 auto',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              background:
                'linear-gradient(135deg, rgba(255,224,130,0.08), rgba(255,255,255,0.015) 36%, rgba(170,124,255,0.07) 72%, rgba(255,224,130,0.04))',
              opacity: commentGlowActive ? 1 : 0.7,
              transition: 'opacity 180ms ease',
            }}
          />
          <span
            aria-hidden="true"
            style={{
              ...iconWrapStyle,
              width: 22,
              height: 22,
              background: commentGlowActive ? 'rgba(255,255,255,0.09)' : 'rgba(255,255,255,0.06)',
              boxShadow: commentGlowActive
                ? '0 0 0 1px rgba(255,255,255,0.06) inset, 0 0 10px rgba(255,224,130,0.06)'
                : '0 0 0 1px rgba(255,255,255,0.04) inset',
              position: 'relative',
              zIndex: 1,
              fontSize: 12,
              fontWeight: 1000,
              color: '#FFF7DA',
              transform: commentGlowActive ? 'scale(1.03)' : 'scale(1)',
            }}
          >
            💬
            {commentCount > 0 ? (
              <span
                style={{
                  position: 'absolute',
                  top: -5,
                  right: -7,
                  minWidth: 16,
                  height: 16,
                  padding: '0 4px',
                  borderRadius: 999,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #FFE28A 0%, #FFD76A 38%, #FFB938 100%)',
                  border: '1px solid rgba(255,224,130,0.52)',
                  boxShadow: '0 6px 12px rgba(0,0,0,0.18), 0 0 8px rgba(255,224,130,0.18)',
                  color: '#2E1A00',
                  fontSize: 9.5,
                  fontWeight: 1000,
                  lineHeight: 1,
                }}
              >
                {commentCount > 99 ? '99+' : commentCount}
              </span>
            ) : null}
          </span>
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          flex: '0 0 auto',
                    position: 'relative',
          zIndex: 1,
        }}
      >
        {menuFeedback ? (
          <span
            style={{
              padding: '5px 8px',
              borderRadius: 999,
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.06)',
              color: 'rgba(255,245,220,0.82)',
              fontSize: 10.5,
              fontWeight: 900,
              letterSpacing: 0.08,
              whiteSpace: 'nowrap',
            }}
          >
            {menuFeedback}
          </span>
        ) : null}

                <div ref={menuRef} style={{ position: 'relative', flex: '0 0 auto', zIndex: menuOpen ? 5002 : 1 }}>
          <button
            type="button"
            onClick={() => {
  setMenuOpen(prev => !prev);
}}
            onMouseEnter={(event) => {
              setHoveredAction('menu');
              applyHoverIn(event);
            }}
            onMouseLeave={(event) => {
              setHoveredAction((prev) => (prev === 'menu' ? '' : prev));
              applyHoverOut(event);
            }}
            onMouseDown={(event) => {
              setPressedAction('menu');
              applyPressIn(event);
            }}
            onMouseUp={(event) => {
              setPressedAction((prev) => (prev === 'menu' ? '' : prev));
              applyPressOut(event);
            }}
            onTouchStart={(event) => {
              setPressedAction('menu');
              applyPressIn(event);
            }}
            onTouchEnd={(event) => {
              setPressedAction((prev) => (prev === 'menu' ? '' : prev));
              applyPressOut(event);
            }}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            style={{
              ...socialButtonStyle,
              minWidth: isMobile ? 34 : 36,
              minHeight: isMobile ? 32 : 34,
              padding: isMobile ? '6px 8px' : '6px 10px',
              position: 'relative',
              overflow: 'hidden',
              background: menuOpen
                ? 'linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.055))'
                : menuGlowActive
                  ? 'linear-gradient(180deg, rgba(255,255,255,0.09), rgba(255,255,255,0.045))'
                  : 'rgba(255,255,255,0.035)',
              border: menuGlowActive
                ? '1px solid rgba(255,224,130,0.12)'
                : '1px solid rgba(255,255,255,0.06)',
              color: 'rgba(247,242,255,0.90)',
              boxShadow: menuOpen
                ? '0 10px 18px rgba(0,0,0,0.10), 0 0 14px rgba(255,224,130,0.06)'
                : menuGlowActive
                  ? '0 10px 18px rgba(0,0,0,0.08), 0 0 12px rgba(255,224,130,0.05)'
                  : 'none',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                background:
                  'linear-gradient(135deg, rgba(255,224,130,0.08), rgba(255,255,255,0.015) 36%, rgba(170,124,255,0.07) 72%, rgba(255,224,130,0.04))',
                opacity: menuGlowActive ? 1 : 0.72,
                transition: 'opacity 180ms ease',
              }}
            />
            <span
              aria-hidden="true"
              style={{
                position: 'relative',
                zIndex: 1,
                fontSize: 18,
                lineHeight: 1,
                transform: menuGlowActive ? 'scale(1.04)' : 'scale(1)',
                transition: 'transform 180ms cubic-bezier(0.22, 1, 0.36, 1)',
              }}
            >
              ⋯
            </span>
          </button>

          {menuOpen ? (
            isMobile ? (
              canUsePortal ? createPortal(
                <>
                  <div
                    onClick={() => {
                      setMenuOpen(false);
                      setShareSheetOpen(false);
                      setReportComposerOpen(false);
                      setReportDetails('');
                    }}
                    style={{
                      position: 'fixed',
                      inset: 0,
                      background: 'rgba(6, 3, 18, 0.62)',
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      zIndex: 4998,
                    }}
                  />

                  <div
                    ref={mobileSheetRef}
                    role="menu"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                    style={{
                      position: 'fixed',
                      left: 14,
                      right: 14,
                      bottom: 'max(14px, calc(env(safe-area-inset-bottom, 0px) + 14px))',
                      top: 'auto',
                      minWidth: 0,
                      width: 'auto',
                      maxWidth: 'none',
                      maxHeight: 'min(78vh, 760px)',
                      overflowY: 'auto',
                      overscrollBehavior: 'contain',
                      borderRadius: 24,
                      border: '1px solid rgba(255,255,255,0.08)',
                      background: 'linear-gradient(180deg, rgba(25,9,42,0.985), rgba(18,5,31,0.985))',
                      boxShadow: '0 34px 80px rgba(0,0,0,0.46), 0 0 0 1px rgba(255,255,255,0.03) inset, 0 0 34px rgba(255,215,0,0.06)',
                      animation: 'feed-post-actions-sheet-in 220ms cubic-bezier(0.22, 1, 0.36, 1)',
                      padding: 12,
                      zIndex: 4999,
                      backdropFilter: 'blur(14px)',
                      WebkitBackdropFilter: 'blur(14px)',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        padding: '4px 6px 10px',
                      }}
                    >
                      <span
                        style={{
                          color: '#FFF2C2',
                          fontSize: 15.5,
                          fontWeight: 1000,
                          letterSpacing: -0.18,
                        }}
                      >
                        {feedText('Opciones de publicación', 'Post options')}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setMenuOpen(false);
                          setShareSheetOpen(false);
                          setReportComposerOpen(false);
                          setReportDetails('');
                        }}
                        style={{
                          ...actionBaseStyle,
                          border: '1px solid rgba(255,255,255,0.10)',
                          background: 'rgba(255,255,255,0.065)',
                          color: '#FFF8E1',
                          borderRadius: 999,
                          padding: '8px 13px',
                          fontSize: 12,
                          fontWeight: 800,
                          cursor: 'pointer',
                        }}
                      >
                        {feedText('Cerrar', 'Close')}
                      </button>
                    </div>

                    <button
                      type="button"
                      role="menuitem"
                      onClick={handleOpenShareSheet}
                      style={{
                        ...actionBaseStyle,
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        borderRadius: 13,
                        padding: '10px 11px',
                        cursor: 'pointer',
                        background: shareSheetOpen
                          ? 'rgba(255,255,255,0.07)'
                          : 'rgba(255,255,255,0.035)',
                        border: shareSheetOpen
                          ? '1px solid rgba(170,124,255,0.16)'
                          : '1px solid rgba(255,255,255,0.06)',
                        color: '#F6F0FF',
                        fontWeight: 900,
                        fontSize: 12,
                        marginBottom: 6,
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <span aria-hidden="true" style={{ width: 22, textAlign: 'center', fontSize: 13 }}>↗</span>
                        Compartir en redes
                      </span>
                      <span
                        aria-hidden="true"
                        style={{
                          fontSize: 12,
                          opacity: 0.88,
                          transform: shareSheetOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 180ms ease',
                        }}
                      >
                        ▾
                      </span>
                    </button>

                    {shareSheetOpen ? (
                      <div
                        style={{
                          marginBottom: 8,
                          borderRadius: 16,
                          border: '1px solid rgba(255,224,130,0.08)',
                          background: 'linear-gradient(180deg, rgba(255,255,255,0.055), rgba(255,255,255,0.022))',
                          boxShadow: '0 14px 28px rgba(0,0,0,0.18), 0 0 0 1px rgba(255,255,255,0.02) inset, 0 0 24px rgba(170,124,255,0.08)',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                            gap: 9,
                            padding: 10,
                          }}
                        >
                          <button type="button" onClick={handleShareWhatsapp} onMouseEnter={applyTileHoverIn} onMouseLeave={applyTileHoverOut} style={shareTileStyle}>
                            <span aria-hidden="true" style={{ ...shareTileIconWrapStyle, background: 'linear-gradient(135deg, rgba(37,211,102,0.22), rgba(255,255,255,0.05))' }}>🟢</span>
                            <span>WhatsApp</span>
                          </button>
                          <button type="button" onClick={handleShareFacebook} onMouseEnter={applyTileHoverIn} onMouseLeave={applyTileHoverOut} style={shareTileStyle}>
                            <span aria-hidden="true" style={{ ...shareTileIconWrapStyle, background: 'linear-gradient(135deg, rgba(24,119,242,0.22), rgba(255,255,255,0.05))' }}>f</span>
                            <span>Facebook</span>
                          </button>
                          <button type="button" onClick={handleShareTwitter} onMouseEnter={applyTileHoverIn} onMouseLeave={applyTileHoverOut} style={shareTileStyle}>
                            <span aria-hidden="true" style={{ ...shareTileIconWrapStyle, background: 'linear-gradient(135deg, rgba(255,255,255,0.14), rgba(255,255,255,0.04))' }}>𝕏</span>
                            <span>X</span>
                          </button>
                          <button type="button" onClick={handleShareTelegram} onMouseEnter={applyTileHoverIn} onMouseLeave={applyTileHoverOut} style={shareTileStyle}>
                            <span aria-hidden="true" style={{ ...shareTileIconWrapStyle, background: 'linear-gradient(135deg, rgba(36,162,224,0.22), rgba(255,255,255,0.05))' }}>✈</span>
                            <span>Telegram</span>
                          </button>
                        </div>
                      </div>
                    ) : null}

                    <button
                      type="button"
                      role="menuitem"
                      onClick={handleCopyLink}
                      style={{
                        ...actionBaseStyle,
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        borderRadius: 13,
                        padding: '10px 11px',
                        cursor: 'pointer',
                        background: 'rgba(255,255,255,0.035)',
                        border: '1px solid rgba(255,255,255,0.06)',
                        color: '#F6F0FF',
                        fontWeight: 900,
                        fontSize: 12,
                        marginBottom: 6,
                      }}
                    >
                      <span aria-hidden="true" style={{ width: 22, textAlign: 'center', fontSize: 13 }}>🔗</span>
                      Copiar enlace
                    </button>

                    <button
                      type="button"
                      role="menuitem"
                      onClick={handleOpenReportComposer}
                      disabled={reporting}
                      style={{
                        ...actionBaseStyle,
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 10,
                        borderRadius: 13,
                        padding: '10px 11px',
                        cursor: reporting ? 'default' : 'pointer',
                        background: reportComposerOpen
                          ? 'rgba(255,255,255,0.07)'
                          : 'rgba(255,255,255,0.035)',
                        border: reportComposerOpen
                          ? '1px solid rgba(255,92,138,0.16)'
                          : '1px solid rgba(255,255,255,0.06)',
                        color: '#F6F0FF',
                        fontWeight: 900,
                        fontSize: 12,
                        marginBottom: reportComposerOpen || isOwner ? 6 : 0,
                        opacity: reporting ? 0.78 : 1,
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <span aria-hidden="true" style={{ width: 22, textAlign: 'center', fontSize: 13 }}>⚑</span>
                        {reporting ? feedText('Reportando...', 'Reporting...') : feedText('Reportar', 'Report')}
                      </span>
                      <span
                        aria-hidden="true"
                        style={{
                          fontSize: 12,
                          opacity: 0.88,
                          transform: reportComposerOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 180ms ease',
                        }}
                      >
                        ▾
                      </span>
                    </button>

                    {reportComposerOpen ? (
                      <div
                        style={{
                          marginBottom: isOwner ? 6 : 0,
                          borderRadius: 14,
                          border: '1px solid rgba(255,255,255,0.06)',
                          background: 'linear-gradient(180deg, rgba(255,255,255,0.045), rgba(255,255,255,0.02))',
                          boxShadow: '0 12px 22px rgba(0,0,0,0.14), 0 0 0 1px rgba(255,255,255,0.02) inset',
                          padding: 10,
                        }}
                      >
                        <div style={{ color: 'rgba(245,240,255,0.82)', fontSize: 12, fontWeight: 800, letterSpacing: 0.12, marginBottom: 6 }}>
                          {feedText('Contanos brevemente qué pasó', 'Briefly tell us what happened')} <span style={{ opacity: 0.6 }}>{feedText('(opcional)', '(optional)')}</span>
                        </div>
                        <textarea
                          id={`feed-report-details-${post?.id || post?._id || 'post'}`}
                          name="feedReportDetails"
                          value={reportDetails}
                          onChange={(event) => setReportDetails(String(event.target.value || '').slice(0, 180))}
                          placeholder={feedText('Ej: spam repetido, insultos, contenido engañoso...', 'E.g. repeated spam, insults, misleading content...')}
                          rows={3}
                          maxLength={180}
                          style={{ width: '100%', minHeight: 74, resize: 'none', borderRadius: 14, border: '1px solid rgba(255,224,130,0.10)', background: 'rgba(255,255,255,0.06)', color: '#F8F4FF', padding: '10px 12px', outline: 'none', fontSize: 13, lineHeight: 1.45, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)' }}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 8 }}>
                          <div style={{ color: 'rgba(245,240,255,0.52)', fontSize: 11, fontWeight: 700 }}>{reportDetails.length}/180</div>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            <button type="button" onClick={handleCloseReportComposer} style={{ ...actionBaseStyle, borderRadius: 999, padding: '8px 11px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,224,130,0.08)', color: 'rgba(246,240,255,0.86)', fontWeight: 800, fontSize: 12, cursor: 'pointer' }}>{feedText('Cancelar', 'Cancel')}</button>
                            <button type="button" onClick={handleReport} disabled={reporting} style={{ ...actionBaseStyle, borderRadius: 999, padding: '8px 12px', background: 'linear-gradient(135deg, rgba(255,92,138,0.20), rgba(255,224,130,0.12))', border: '1px solid rgba(255,92,138,0.20)', color: '#FFE7EE', fontWeight: 900, fontSize: 12, cursor: reporting ? 'default' : 'pointer', opacity: reporting ? 0.78 : 1 }}>{reporting ? feedText('Enviando...', 'Sending...') : feedText('Enviar reporte', 'Send report')}</button>
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {isOwner ? (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={handleDelete}
                        onMouseEnter={applyHoverIn}
                        onMouseLeave={applyHoverOut}
                        onMouseDown={applyPressIn}
                        onMouseUp={applyPressOut}
                        onTouchStart={applyPressIn}
                        onTouchEnd={applyPressOut}
                        disabled={deleting || liking}
                        style={{
                          ...actionBaseStyle,
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          borderRadius: 13,
                          padding: '10px 11px',
                          cursor: 'pointer',
                          background: 'linear-gradient(180deg, rgba(255,88,88,0.08), rgba(255,255,255,0.02))',
                          border: '1px solid rgba(255,88,88,0.12)',
                          color: '#FFD7D7',
                          fontWeight: 900,
                          fontSize: 12,
                          boxShadow: '0 8px 16px rgba(0,0,0,0.08)',
                          transition: 'transform 180ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 180ms ease, opacity 180ms ease, filter 180ms ease',
                          opacity: deleting ? 0.78 : 1,
                        }}
                      >
                        <span aria-hidden="true" style={{ width: 22, height: 22, borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, rgba(255,88,88,0.16), rgba(255,255,255,0.04))', boxShadow: '0 0 0 1px rgba(255,88,88,0.12) inset', flex: '0 0 auto' }}>
                          <span style={{ fontSize: 12, lineHeight: 1 }}>🗑</span>
                        </span>
                        {deleting ? feedText('Eliminando...', 'Deleting...') : feedText('Eliminar publicación', 'Delete post')}
                      </button>
                    ) : null}
                  </div>
                </>,
                document.body
              ) : null
            ) : (
              <div
                role="menu"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
                style={{
                  position: 'absolute',
                  right: 0,
                  bottom: 'calc(100% + 8px)',
                  width: 320,
                  maxWidth: 'min(320px, calc(100vw - 24px))',
                  maxHeight: 'min(72vh, 560px)',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  overscrollBehavior: 'contain',
                  borderRadius: 18,
                  border: '1px solid rgba(255,255,255,0.08)',
                  background: 'linear-gradient(180deg, rgba(25,9,42,0.985), rgba(18,5,31,0.985))',
                  boxShadow: '0 22px 38px rgba(0,0,0,0.28), 0 0 0 1px rgba(255,255,255,0.025) inset, 0 0 24px rgba(255,215,0,0.05)',
                  animation: 'feed-post-actions-pop-in-up 180ms cubic-bezier(0.22, 1, 0.36, 1)',
                  padding: 8,
                  zIndex: 5001,
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  transformOrigin: 'bottom right',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: '4px 6px 10px',
                  }}
                >
                  <span
                    style={{
                      color: '#FFF2C2',
                      fontSize: 14,
                      fontWeight: 1000,
                      letterSpacing: -0.14,
                    }}
                  >
                    Opciones
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      setShareSheetOpen(false);
                      setReportComposerOpen(false);
                      setReportDetails('');
                    }}
                    style={{
                      ...actionBaseStyle,
                      border: '1px solid rgba(255,255,255,0.10)',
                      background: 'rgba(255,255,255,0.065)',
                      color: '#FFF8E1',
                      borderRadius: 999,
                      padding: '7px 11px',
                      fontSize: 11.5,
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    {feedText('Cerrar', 'Close')}
                  </button>
                </div>

                <button
                  type="button"
                  role="menuitem"
                  onClick={handleOpenShareSheet}
                  style={{
                    ...actionBaseStyle,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    borderRadius: 13,
                    padding: '10px 11px',
                    cursor: 'pointer',
                    background: shareSheetOpen ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.035)',
                    border: shareSheetOpen ? '1px solid rgba(170,124,255,0.16)' : '1px solid rgba(255,255,255,0.06)',
                    color: '#F6F0FF',
                    fontWeight: 900,
                    fontSize: 12,
                    marginBottom: 6,
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                    <span aria-hidden="true" style={{ width: 22, textAlign: 'center', fontSize: 13 }}>↗</span>
                    Compartir en redes
                  </span>
                  <span
                    aria-hidden="true"
                    style={{
                      fontSize: 12,
                      opacity: 0.88,
                      transform: shareSheetOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 180ms ease',
                    }}
                  >
                    ▾
                  </span>
                </button>

                {shareSheetOpen ? (
                  <div
                    style={{
                      marginBottom: 8,
                      borderRadius: 16,
                      border: '1px solid rgba(255,224,130,0.08)',
                      background: 'linear-gradient(180deg, rgba(255,255,255,0.055), rgba(255,255,255,0.022))',
                      boxShadow: '0 14px 28px rgba(0,0,0,0.18), 0 0 0 1px rgba(255,255,255,0.02) inset, 0 0 24px rgba(170,124,255,0.08)',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        gap: 9,
                        padding: 10,
                      }}
                    >
                      <button type="button" onClick={handleShareWhatsapp} onMouseEnter={applyTileHoverIn} onMouseLeave={applyTileHoverOut} style={shareTileStyle}>
                        <span aria-hidden="true" style={{ ...shareTileIconWrapStyle, background: 'linear-gradient(135deg, rgba(37,211,102,0.22), rgba(255,255,255,0.05))' }}>🟢</span>
                        <span>WhatsApp</span>
                      </button>
                      <button type="button" onClick={handleShareFacebook} onMouseEnter={applyTileHoverIn} onMouseLeave={applyTileHoverOut} style={shareTileStyle}>
                        <span aria-hidden="true" style={{ ...shareTileIconWrapStyle, background: 'linear-gradient(135deg, rgba(24,119,242,0.22), rgba(255,255,255,0.05))' }}>f</span>
                        <span>Facebook</span>
                      </button>
                      <button type="button" onClick={handleShareTwitter} onMouseEnter={applyTileHoverIn} onMouseLeave={applyTileHoverOut} style={shareTileStyle}>
                        <span aria-hidden="true" style={{ ...shareTileIconWrapStyle, background: 'linear-gradient(135deg, rgba(255,255,255,0.14), rgba(255,255,255,0.04))' }}>𝕏</span>
                        <span>X</span>
                      </button>
                      <button type="button" onClick={handleShareTelegram} onMouseEnter={applyTileHoverIn} onMouseLeave={applyTileHoverOut} style={shareTileStyle}>
                        <span aria-hidden="true" style={{ ...shareTileIconWrapStyle, background: 'linear-gradient(135deg, rgba(36,162,224,0.22), rgba(255,255,255,0.05))' }}>✈</span>
                        <span>Telegram</span>
                      </button>
                    </div>
                  </div>
                ) : null}

                <button
                  type="button"
                  role="menuitem"
                  onClick={handleCopyLink}
                  style={{
                    ...actionBaseStyle,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    borderRadius: 13,
                    padding: '10px 11px',
                    cursor: 'pointer',
                    background: 'rgba(255,255,255,0.035)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    color: '#F6F0FF',
                    fontWeight: 900,
                    fontSize: 12,
                    marginBottom: 6,
                  }}
                >
                  <span aria-hidden="true" style={{ width: 22, textAlign: 'center', fontSize: 13 }}>🔗</span>
                  Copiar enlace
                </button>

                <button
                  type="button"
                  role="menuitem"
                  onClick={handleOpenReportComposer}
                  disabled={reporting}
                  style={{
                    ...actionBaseStyle,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                    borderRadius: 13,
                    padding: '10px 11px',
                    cursor: reporting ? 'default' : 'pointer',
                    background: reportComposerOpen ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.035)',
                    border: reportComposerOpen ? '1px solid rgba(255,92,138,0.16)' : '1px solid rgba(255,255,255,0.06)',
                    color: '#F6F0FF',
                    fontWeight: 900,
                    fontSize: 12,
                    marginBottom: reportComposerOpen || isOwner ? 6 : 0,
                    opacity: reporting ? 0.78 : 1,
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                    <span aria-hidden="true" style={{ width: 22, textAlign: 'center', fontSize: 13 }}>⚑</span>
                    {reporting ? feedText('Reportando...', 'Reporting...') : feedText('Reportar', 'Report')}
                  </span>
                  <span aria-hidden="true" style={{ fontSize: 12, opacity: 0.88, transform: reportComposerOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 180ms ease' }}>▾</span>
                </button>

                {reportComposerOpen ? (
                  <div
                    style={{
                      marginBottom: isOwner ? 6 : 0,
                      borderRadius: 14,
                      border: '1px solid rgba(255,255,255,0.06)',
                      background: 'linear-gradient(180deg, rgba(255,255,255,0.045), rgba(255,255,255,0.02))',
                      boxShadow: '0 12px 22px rgba(0,0,0,0.14), 0 0 0 1px rgba(255,255,255,0.02) inset',
                      padding: 10,
                    }}
                  >
                    <div style={{ color: 'rgba(245,240,255,0.82)', fontSize: 12, fontWeight: 800, letterSpacing: 0.12, marginBottom: 6 }}>
                      {feedText('Contanos brevemente qué pasó', 'Briefly tell us what happened')} <span style={{ opacity: 0.6 }}>{feedText('(opcional)', '(optional)')}</span>
                    </div>
                    <textarea
                      id={`feed-report-details-${post?.id || post?._id || 'post'}`}
                      name="feedReportDetails"
                      value={reportDetails}
                      onChange={(event) => setReportDetails(String(event.target.value || '').slice(0, 180))}
                      placeholder={feedText('Ej: spam repetido, insultos, contenido engañoso...', 'E.g. repeated spam, insults, misleading content...')}
                      rows={3}
                      maxLength={180}
                      style={{ width: '100%', minHeight: 74, resize: 'none', borderRadius: 14, border: '1px solid rgba(255,224,130,0.10)', background: 'rgba(255,255,255,0.06)', color: '#F8F4FF', padding: '10px 12px', outline: 'none', fontSize: 13, lineHeight: 1.45, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)' }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 8 }}>
                      <div style={{ color: 'rgba(245,240,255,0.52)', fontSize: 11, fontWeight: 700 }}>{reportDetails.length}/180</div>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <button type="button" onClick={handleCloseReportComposer} style={{ ...actionBaseStyle, borderRadius: 999, padding: '8px 11px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,224,130,0.08)', color: 'rgba(246,240,255,0.86)', fontWeight: 800, fontSize: 12, cursor: 'pointer' }}>{feedText('Cancelar', 'Cancel')}</button>
                        <button type="button" onClick={handleReport} disabled={reporting} style={{ ...actionBaseStyle, borderRadius: 999, padding: '8px 12px', background: 'linear-gradient(135deg, rgba(255,92,138,0.20), rgba(255,224,130,0.12))', border: '1px solid rgba(255,92,138,0.20)', color: '#FFE7EE', fontWeight: 900, fontSize: 12, cursor: reporting ? 'default' : 'pointer', opacity: reporting ? 0.78 : 1 }}>{reporting ? feedText('Enviando...', 'Sending...') : feedText('Enviar reporte', 'Send report')}</button>
                      </div>
                    </div>
                  </div>
                ) : null}

                {isOwner ? (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={handleDelete}
                    onMouseEnter={applyHoverIn}
                    onMouseLeave={applyHoverOut}
                    onMouseDown={applyPressIn}
                    onMouseUp={applyPressOut}
                    onTouchStart={applyPressIn}
                    onTouchEnd={applyPressOut}
                    disabled={deleting || liking}
                    style={{
                      ...actionBaseStyle,
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      borderRadius: 13,
                      padding: '10px 11px',
                      cursor: 'pointer',
                      background: 'linear-gradient(180deg, rgba(255,88,88,0.08), rgba(255,255,255,0.02))',
                      border: '1px solid rgba(255,88,88,0.12)',
                      color: '#FFD7D7',
                      fontWeight: 900,
                      fontSize: 12,
                      boxShadow: '0 8px 16px rgba(0,0,0,0.08)',
                      transition: 'transform 180ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 180ms ease, opacity 180ms ease, filter 180ms ease',
                      opacity: deleting ? 0.78 : 1,
                    }}
                  >
                    <span aria-hidden="true" style={{ width: 22, height: 22, borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, rgba(255,88,88,0.16), rgba(255,255,255,0.04))', boxShadow: '0 0 0 1px rgba(255,88,88,0.12) inset', flex: '0 0 auto' }}>
                      <span style={{ fontSize: 12, lineHeight: 1 }}>🗑</span>
                    </span>
                    {deleting ? feedText('Eliminando...', 'Deleting...') : feedText('Eliminar publicación', 'Delete post')}
                  </button>
                ) : null}
              </div>
            )
          ) : null}
        </div>
      </div>
      <style>{`
        .feed-post-actions > div::-webkit-scrollbar {
          display: none;
        }

        .feed-post-actions [role="menu"] {
          pointer-events: auto;
        }

        .feed-post-actions [role="menu"]::-webkit-scrollbar {
          width: 8px;
        }

        .feed-post-actions [role="menu"]::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.12);
          border-radius: 999px;
        }

        .feed-post-actions [role="menu"] {
          scrollbar-gutter: stable;
        }

        @keyframes feed-post-actions-pop-in {
          0% {
            opacity: 0;
            transform: translateY(6px) scale(0.985);
          }
          100% {
            opacity: 1;
            transform: translateY(0px) scale(1);
          }
        }

        @keyframes feed-post-actions-pop-in-up {
          0% {
            opacity: 0;
            transform: translateY(6px) scale(0.985);
          }
          100% {
            opacity: 1;
            transform: translateY(0px) scale(1);
          }
        }

        @keyframes feed-post-actions-sheet-in {
          0% {
            opacity: 0;
            transform: translateY(18px) scale(0.988);
          }
          100% {
            opacity: 1;
            transform: translateY(0px) scale(1);
          }
        }

        @keyframes feed-post-actions-burst {
          0% {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.35);
          }
          20% {
            opacity: 0.22;
          }
          100% {
            opacity: 0;
            transform: translate(-50%, -50%) scale(1.55);
          }
        }

        .feed-post-actions__burst {
          position: absolute;
          left: 50%;
          top: 50%;
          width: 54px;
          height: 54px;
          border-radius: 999px;
          pointer-events: none;
          background: radial-gradient(circle, rgba(255,224,130,0.20) 0%, rgba(255,224,130,0.10) 28%, rgba(255,224,130,0) 72%);
          transform: translate(-50%, -50%) scale(0.35);
          opacity: 0;
          z-index: 0;
        }

        .feed-post-actions__burst.is-active {
          animation: feed-post-actions-burst 520ms cubic-bezier(0.22, 1, 0.36, 1);
        }

        @media (max-width: 640px) {
          .feed-post-actions {
            width: 100%;
          }
          body {
            -webkit-overflow-scrolling: auto;
          }

          .feed-post-actions,
          .feed-post-actions > div,
          .feed-post-actions [role="menu"] {
            isolation: auto;
          }

          .feed-post-actions__burst,
          .feed-post-actions [role="menu"] {
            animation-duration: 0.001ms !important;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .feed-post-actions,
          .feed-post-actions button,
          .feed-post-actions [role="menu"],
          .feed-post-actions__burst {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>
    </div>
  );
}
